import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Control, Errors, Form } from 'react-redux-form';

// Models
import { AgentSearchViewModel, RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';
import { ContractViewModel, ContractOrderType } from '@models/Contract';
import { PageViewModel } from '@models/common.models';
import { RealEstateAgent } from 'src/realestate-agent/_components/realestate-agent.container';

// Form Validation
import { getDefaultErrorMessages } from '@common/validators';
import { dateParser } from '@common/dateParser';

// Components
import { Option } from 'react-select';
import { TypeAheadComponent } from '@common/components/typeahead/typeahead.component';
import { ModalProps } from '@common/components/modal';
import { PropertyManagerSearchViewModel } from '@models/PropertyManager';
import { PropertyManager } from 'src/property-manager';
import { EscrowInfo } from '../escrow-info/escrow-info.container';
import { EditPermissions, EditableFields } from '@models/EditPermissions';

export interface AffiliatesInfoComponentProperties extends RouteComponentProps<{ id?: number }> {
  readonly message: string;
  readonly currentStep: number;
  readonly isRenewal?: boolean;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly onSaveClick: (model: ContractViewModel) => void;
  readonly contractVM: ContractViewModel | null;
  readonly agentLookup: (query: string, id?: number) => Promise<PageViewModel<AgentSearchViewModel>>;
  readonly onInitAgentSelect: (model: string, value: number) => void;
  readonly initAgent?: RealEstateAgentViewModel;
  readonly initOffice?: RealEstateOfficeViewModel;
  readonly onCoopAgentSelect: (model: string, value: number) => void;
  readonly coopAgent?: RealEstateAgentViewModel;
  readonly coopOffice?: RealEstateOfficeViewModel;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly clearInitAgent: () => void;
  readonly clearCoopAgent: () => void;
  readonly additionalRealEstateAgents: Array<RealEstateAgentViewModel>;
  readonly onRealEstateAgentSelect: (model: string, value: number) => void;
  readonly removeRealEstateAgent: (index: number) => void;
  readonly propertyManagerLookup: (
    query: string,
    id?: number
  ) => Promise<PageViewModel<PropertyManagerSearchViewModel>>;
  readonly onPropertyManagerSelect: (model: string, value: number) => void;
  readonly propertyManager?: PropertyManagerSearchViewModel;
  readonly clearPropertyManager: () => void;

  readonly orderType?: ContractOrderType;
  readonly isOrderEdit?: boolean;
  readonly isOpenContract?: boolean;
}

export class AffiliatesInfoComponent extends React.Component<AffiliatesInfoComponentProperties> {
  constructor(props: AffiliatesInfoComponentProperties) {
    super(props);
    this.showInitRealtorModal = this.showInitRealtorModal.bind(this);
    this.showCoopRealtorModal = this.showCoopRealtorModal.bind(this);
    this.showRealEstateAgentModal = this.showRealEstateAgentModal.bind(this);
  }

  showInitRealtorModal() {
    if (this.props.initAgent) {
      this.props.toggleModal({
        title: 'Edit Initiating Real Estate Agent',
        component: RealEstateAgent,
        saveCallback: this.loadInitAgent.bind(this),
        childProps: { entityId: this.props.initAgent.id }
      });
    } else {
      this.props.toggleModal({
        title: 'Add New Initiating Real Estate Agent',
        component: RealEstateAgent,
        saveCallback: this.loadInitAgent.bind(this)
      });
    }
  }

  loadInitAgent(id: number) {
    this.props.toggleModal();
    this.props.onInitAgentSelect('', id);
  }

  showCoopRealtorModal() {
    if (this.props.coopAgent) {
      this.props.toggleModal({
        title: 'Edit Cooperating Real Estate Agent',
        component: RealEstateAgent,
        saveCallback: this.loadCoopAgent.bind(this),
        childProps: { entityId: this.props.coopAgent.id }
      });
    } else {
      this.props.toggleModal({
        title: 'Add New Cooperating Real Estate Agent',
        component: RealEstateAgent,
        saveCallback: this.loadCoopAgent.bind(this)
      });
    }
  }

  loadCoopAgent(id: number) {
    this.props.toggleModal();
    this.props.onCoopAgentSelect('', id);
  }

  showRealEstateAgentModal(id?: number) {
    if (id) {
      this.props.toggleModal({
        title: 'Edit Additional Real Estate Agent',
        component: RealEstateAgent,
        saveCallback: this.loadRealEstateAgent.bind(this),
        childProps: { entityId: id }
      });
    } else {
      this.props.toggleModal({
        title: 'Add New Additional Real Estate Agent',
        component: RealEstateAgent,
        saveCallback: this.loadRealEstateAgent.bind(this)
      });
    }
  }

  loadRealEstateAgent(id: number) {
    this.props.toggleModal();
    this.props.onRealEstateAgentSelect('', id);
  }

  handleRemoveRealEstateAgent = (index: number) => (e: React.MouseEvent<HTMLHtmlElement>) => {
    this.props.removeRealEstateAgent(index);
  };

  renderRealEstateAgents(agents: Array<RealEstateAgentViewModel>) {
    return agents.map((agent, index) => {
      return (
        <div key={index} className="row border-top-bottom mt-5">
          <div className="col-md-12">
            <div className="row">
              <div className="col-xs-11 text-right">
                {this.props.isOrderEdit ? (
                  EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract) && (
                    <i className="fa fa-times pointer mt-5" onClick={this.handleRemoveRealEstateAgent(index)} />
                  )
                ) : (
                  <i className="fa fa-times pointer mt-5" onClick={this.handleRemoveRealEstateAgent(index)} />
                )}
              </div>
              <div className="col-xs-1">
                {this.props.isOrderEdit ? (
                  EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract) && (
                    <i className="fa fa-edit pointer mt-5" onClick={() => this.showRealEstateAgentModal(agent.id)} />
                  )
                ) : (
                  <i className="fa fa-edit pointer mt-5" onClick={() => this.showRealEstateAgentModal(agent.id)} />
                )}
              </div>
            </div>
            <div className="row">{this.renderAgent(agent, agent.realEstateOffice)}</div>
          </div>
        </div>
      );
    });
  }

  renderError() {
    if (!this.props.message) {
      return;
    }
    return <div className="error">{this.props.message}</div>;
  }

  renderAddEditInit() {
    if (!this.props.initAgent) {
      return (
        <button className="btn btn-primary mt-10" type="button" onClick={this.showInitRealtorModal}>
          Add New Agent
        </button>
      );
    }
    return;
  }

  renderAddEditCoop() {
    if (!this.props.coopAgent) {
      return (
        <button
          className="btn btn-primary mt-10"
          type="button"
          onClick={this.showCoopRealtorModal}
          disabled={
            this.props.isOrderEdit && !EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract)
          }
        >
          Add New Agent
        </button>
      );
    }
    return;
  }

  renderEditDeleteAgent(agentId: number, clearAgent: () => void, permission: string) {
    return (
      <div className="row dark-gray-bc">
        <div className="col-xs-11 text-right">
          {this.props.isOrderEdit ? (
            EditPermissions.CanEditField(permission, this.props.isOpenContract) && (
              <i className="fa fa-times pointer mt-5" onClick={() => clearAgent()} />
            )
          ) : (
            <i className="fa fa-times pointer mt-5" onClick={() => clearAgent()} />
          )}
        </div>
        <div className="col-xs-1">
          {this.props.isOrderEdit ? (
            EditPermissions.CanEditField(permission, this.props.isOpenContract) && (
              <i className="fa fa-edit pointer mt-5" onClick={() => this.showRealEstateAgentModal(agentId)} />
            )
          ) : (
            <i className="fa fa-edit pointer mt-5" onClick={() => this.showRealEstateAgentModal(agentId)} />
          )}
        </div>
      </div>
    );
  }

  renderAgent(agent?: RealEstateAgentViewModel, office?: RealEstateOfficeViewModel) {
    if (!agent) {
      return;
    }
    return (
      <div className="col-md-12 mb-10 pl-15">
        <div className="row mt-10">
          <div className="col-md-6 pl-15">
            <strong>{agent.name}</strong>
          </div>
          <div className="col-md-6">
            <strong>{office ? office.name : ''}</strong>
          </div>
        </div>
        <div className="row mt-10">
          <div className="col-md-6 pl-15 ellipsis">{agent.emailAddress}</div>
          <div className="col-md-6">Office Phone: {office ? office.phone : ''}</div>
        </div>
        <div className="row mt-10">
          <div className="col-md-6 pl-15">Agent Phone: {agent.directPhone}</div>
          <div className="col-md-6">Office Fax: {office ? office.fax : ''}</div>
        </div>
        <div className="row mt-10">
          <div className="col-md-6 pl-15">Agent Fax: {agent.directFax}</div>
          <div className="col-md-6">
            Location: {office && office!.address ? office.address.city + ', ' + office.address.state!.abbreviation : ''}
          </div>
        </div>
        <div className="row mt-10 mb-10">
          <div className="col-md-6 pl-15">Agent Verified: {this.renderDate(agent.verifiedDate)}</div>
        </div>
      </div>
    );
  }

  renderAgentSearch(suggestion: Option<AgentSearchViewModel>) {
    return (
      <div>
        <div>Name: {suggestion.name ? suggestion.name : '—'}</div>
        <div>Phone #: {suggestion.phone ? suggestion.phone : '—'}</div>
        <div>Email: {suggestion.email ? suggestion.email : '—'}</div>
        <div>Office: {suggestion.office ? suggestion.office : '—'}</div>
        <div>Address: {suggestion.addressLine1 ? suggestion.addressLine1 : '-'}</div>
        <div>City: {suggestion.city ? suggestion.city : '—'}</div>
      </div>
    );
  }

  renderDate(date?: Date) {
    const formattedDate = date ? dateParser.parse(date).format('MM/DD/YYYY') : '';
    return formattedDate;
  }

  // PropertyManager
  showPropertyManagerModal = () => {
    if (this.props.propertyManager) {
      this.props.toggleModal({
        title: 'Edit Property Manager',
        component: PropertyManager,
        saveCallback: this.loadPropertyManager.bind(this),
        childProps: { entityId: this.props.propertyManager.id }
      });
    } else {
      this.props.toggleModal({
        title: 'Add New Property Manager',
        component: PropertyManager,
        saveCallback: this.loadPropertyManager.bind(this)
      });
    }
  };

  loadPropertyManager(id: number) {
    this.props.toggleModal();
    this.props.onPropertyManagerSelect('', id);
  }

  renderPropertyManagerSection() {
    return (
      <div className="pt-10">
        <div className="row dark-gray-bc">
          <div className="col-md-12">
            <h3>Property Manager</h3>
          </div>
          <div className="col-md-9 mb-10">
            <Control.text
              model=".propertyManagerId"
              className="form-control"
              changeAction={this.props.onPropertyManagerSelect}
              disabled={
                this.props.isOrderEdit &&
                !EditPermissions.CanEditField(EditableFields.PropertyManager, this.props.isOpenContract)
              }
              component={(props: { value: string; onChange: Function }) => {
                return (
                  <TypeAheadComponent
                    onChange={props.onChange}
                    renderValue={this.renderPropertyManagerSearch}
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.PropertyManager, this.props.isOpenContract)
                    }
                    valueKey={'id'}
                    labelKey={'name'}
                    getSuggestions={this.props.propertyManagerLookup}
                  />
                );
              }}
            />
            <Control.text className="txt-hidden" model=".propertyManagerId" />
          </div>
          <div className="col-md-3 text-center">{this.renderAddEditPropertyManager()}</div>
        </div>
        <div className="row dark-gray-bc">{this.renderPropertyManager(this.props.propertyManager)}</div>
      </div>
    );
  }

  renderPropertyManagerSearch(suggestion: Option<PropertyManagerSearchViewModel>) {
    return (
      <div>
        <div>Name: {suggestion.name ? suggestion.name : '—'}</div>
        <div>Phone #: {suggestion.phone ? suggestion.phone : '—'}</div>
        <div>Email: {suggestion.emailAddress ? suggestion.emailAddress : '—'}</div>
        <div>Office: {suggestion.office && suggestion.office.name ? suggestion.office.name : '—'}</div>
        <div>
          Address:{' '}
          {suggestion.office && suggestion.office.address && suggestion.office.address.line1
            ? suggestion.office.address.line1
            : '—'}
        </div>
        <div>
          City:{' '}
          {suggestion.office && suggestion.office.address && suggestion.office.address.city
            ? suggestion.office.address.city
            : '—'}
        </div>
      </div>
    );
  }

  renderPropertyManager(propertyManager?: PropertyManagerSearchViewModel) {
    if (!propertyManager) {
      return;
    }
    return (
      <div className="col-md-12 mb-10">
        <div className="row mt-10 mb-10 pl-15">
          <div className="col-md-3">{propertyManager.name}</div>
          <div className="col-md-3">Phone: {propertyManager.phone}</div>
          <div className="col-md-5 ellipsis">Email: {propertyManager.emailAddress}</div>
        </div>
      </div>
    );
  }

  renderAddEditPropertyManager() {
    if (!this.props.propertyManager) {
      return (
        <button
          className="btn btn-primary mt-5"
          disabled={
            this.props.isOrderEdit &&
            !EditPermissions.CanEditField(EditableFields.PropertyManager, this.props.isOpenContract)
          }
          type="button"
          onClick={this.showPropertyManagerModal}
        >
          Add New Property Manager
        </button>
      );
    }

    return (
      <div className="row">
        <div className="col-md-1 mt-15">
          {this.props.isOrderEdit ? (
            EditPermissions.CanEditField(EditableFields.PropertyManager, this.props.isOpenContract) && (
              <i className="fa fa-times" onClick={this.props.clearPropertyManager} />
            )
          ) : (
            <i className="fa fa-times" onClick={this.props.clearPropertyManager} />
          )}
        </div>
        <div className="col-md-2 mt-5">
          <button
            className="btn btn-primary"
            type="button"
            onClick={this.showPropertyManagerModal}
            disabled={
              this.props.isOrderEdit &&
              !EditPermissions.CanEditField(EditableFields.PropertyManager, this.props.isOpenContract)
            }
          >
            Edit Property Manager
          </button>
        </div>
      </div>
    );
  }

  renderRealtySection() {
    return (
      !this.props.isRenewal && (
        <>
          <div>
            <div className="row dark-gray-bc">
              <div className="col-md-12">
                <h3>Initiating Agent, Realty Office, & SE</h3>
              </div>
              <div className="col-md-9 mb-10">
                <Control.text
                  model=".initAgentId"
                  className="form-control"
                  changeAction={this.props.onInitAgentSelect}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.InitiatingAgent, this.props.isOpenContract)
                  }
                  component={(props: { value: string; onChange: Function }) => {
                    return (
                      <TypeAheadComponent
                        onChange={props.onChange}
                        disabled={
                          this.props.isOrderEdit &&
                          !EditPermissions.CanEditField(EditableFields.InitiatingAgent, this.props.isOpenContract)
                        }
                        renderValue={this.renderAgentSearch}
                        valueKey={'id'}
                        labelKey={'name'}
                        getSuggestions={this.props.agentLookup}
                      />
                    );
                  }}
                />
                <Errors
                  className="error"
                  model=".initAgentId"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
                <Control.text className="txt-hidden" model=".initAgentId" />
                <Control.text className="txt-hidden" model=".initOfficeId" />
              </div>
              <div className="col-md-3 text-center">{this.renderAddEditInit()}</div>
            </div>
            {this.props.initAgent &&
              this.props.initAgent.id &&
              this.renderEditDeleteAgent(
                this.props.initAgent.id,
                this.props.clearInitAgent,
                EditableFields.InitiatingAgent
              )}
            <div className="row dark-gray-bc">{this.renderAgent(this.props.initAgent, this.props.initOffice)}</div>
          </div>
          <div className="mt-3">
            <div className="row mt-10 dark-gray-bc">
              <div className="col-md-12">
                <h3>Cooperating Agent, Realty Office, & SE</h3>
              </div>
              <div className="col-md-9 mb-10">
                <Control.text
                  model=".coopAgentId"
                  className="form-control"
                  changeAction={this.props.onCoopAgentSelect}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract)
                  }
                  component={(props: { value: string; onChange: Function }) => {
                    return (
                      <TypeAheadComponent
                        onChange={props.onChange}
                        disabled={
                          this.props.isOrderEdit &&
                          !EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract)
                        }
                        renderValue={this.renderAgentSearch}
                        valueKey={'id'}
                        labelKey={'name'}
                        getSuggestions={this.props.agentLookup}
                      />
                    );
                  }}
                />
                <Control.text className="txt-hidden" model=".coopAgentId" />
                <Control.text className="txt-hidden" model=".coopOfficeId" />
              </div>
              <div className="col-md-3 text-center">{this.renderAddEditCoop()}</div>
            </div>
            {this.props.coopAgent &&
              this.props.coopAgent.id &&
              this.renderEditDeleteAgent(this.props.coopAgent.id, this.props.clearCoopAgent, EditableFields.CoopAgent)}
            <div className="row dark-gray-bc">{this.renderAgent(this.props.coopAgent, this.props.coopOffice)}</div>
          </div>
          <div className="row pt-10">
            <div className="col-xs-12 dark-gray-bc pb-5">
              <div className="row">
                <div className="col-md-12">
                  <h3>Additional Agent, Realty Office, & SE</h3>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-9 mt-5 mb-10">
                  <Control.text
                    model=".additionaRealEstateAgentIds"
                    className="form-control"
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract)
                    }
                    component={props => {
                      return (
                        <TypeAheadComponent
                          getSuggestions={this.props.agentLookup}
                          disabled={
                            this.props.isOrderEdit &&
                            !EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract)
                          }
                          renderValue={(
                            opt: Option<{ name: string; phone: string; email: string; office: string; city: string }>
                          ) => {
                            return (
                              <div>
                                <div>{opt.name}</div>
                                <div>Phone #: {opt.phone ? opt.phone : '—'}</div>
                                <div>Email: {opt.email ? opt.email : '—'}</div>
                                <div>Office: {opt.office ? opt.office : '—'}</div>
                                <div>City: {opt.city ? opt.city : '—'}</div>
                              </div>
                            );
                          }}
                          valueKey={'id'}
                          labelKey={'name'}
                          onChange={props.onChange}
                        />
                      );
                    }}
                    changeAction={this.props.onRealEstateAgentSelect}
                  />
                  <Control.text className="txt-hidden" model=".additionaRealEstateAgentIds" />
                </div>
                <div className="col-xs-3 text-center">
                  <button
                    type="button"
                    className="btn btn-primary mt-15"
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.CoopAgent, this.props.isOpenContract)
                    }
                    onClick={() => this.showRealEstateAgentModal()}
                  >
                    Add New Agent
                  </button>
                </div>
              </div>
              {this.renderRealEstateAgents(this.props.additionalRealEstateAgents)}
            </div>
          </div>
        </>
      )
    );
  }

  renderEscrowSection() {
    return (
      this.props.orderType === ContractOrderType.RealtyEscrow &&
      !this.props.isRenewal && <EscrowInfo model="affiliatesInfoForm" isOrderEdit={this.props.isOrderEdit} />
    );
  }

  render() {
    return (
      <Form
        model="affiliatesInfoForm"
        className="form-horizontal"
        hideNativeErrors={true}
        onSubmit={this.props.onSaveClick}
      >
        <div className="row">
          <div className="col-md-12 mt-3">
            <h2>Step {this.props.currentStep + 1}: Affiliates Information</h2>
          </div>
        </div>
        {this.renderRealtySection()}
        {this.renderPropertyManagerSection()}
        {this.renderEscrowSection()}
        <div className="col-md-1">
          <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
            Save
          </button>
        </div>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/affiliates-info/affiliates-info.component.tsx